import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../components/common/Button';
import { useNonceValidity, useConnectBot } from '../../hooks/useBotConnection';

/**
 * Phase 73 (Plan 73-04) — nonce-gated Slack bot connect page. One-shot
 * registration form reached from Monk's Slack DM link.
 *
 * - D-02: only a Claude token field renders here. The Monk-named token used
 *   for Monksheets API access is minted silently server-side — there is no
 *   second input field for it, and no such secret is ever displayed
 *   (T-73-15 mitigation).
 * - D-03: an invalid/expired/used nonce renders a friendly "ask Monk
 *   again" panel instead of crashing or showing a raw error.
 * - D-04: numbered setup instructions + backend live-verifies the token
 *   on submit.
 * - D-11: success is storage-only — no promise of an immediate proactive
 *   DM, since the first real reply may take 10-30s to warm up.
 */
export function ConnectPage() {
  const { nonce = '' } = useParams<{ nonce: string }>();
  const { data: validity, isLoading: isCheckingNonce } = useNonceValidity(nonce);
  const connectBot = useConnectBot();

  const [claudeToken, setClaudeToken] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [tokenVerified, setTokenVerified] = useState<boolean | null>(null);
  const [connected, setConnected] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmed = claudeToken.trim();
    if (!trimmed) {
      setFormError('Paste your Claude setup token to continue');
      return;
    }
    connectBot.mutate(
      { nonce, claudeToken: trimmed },
      {
        onSuccess: (result) => {
          setTokenVerified(result.tokenVerified);
          setConnected(true);
        },
        onError: (err: unknown) => {
          setFormError(err instanceof Error ? err.message : 'Failed to connect Monk');
        },
      }
    );
  };

  if (isCheckingNonce) {
    return (
      <div className="max-w-lg mx-auto mt-16 px-4">
        <p className="text-sm text-gray-500">Checking your link...</p>
      </div>
    );
  }

  // D-03 (Phase 75 / ADMIN-02): while the org kill switch is on, the backend
  // forces valid:false + killSwitchActive:true regardless of the nonce's own
  // validity — render a distinct "disabled by admin" panel instead of the
  // generic expired-link message, since retrying with a fresh link wouldn't
  // help here.
  if (validity?.killSwitchActive) {
    return (
      <div className="max-w-lg mx-auto mt-16 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <NoSymbolIcon className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Monk is temporarily disabled by your admins
          </h1>
          <p className="text-sm text-gray-600">
            Bot connections are paused organization-wide right now. Please try again later or
            check with your admin team.
          </p>
        </div>
      </div>
    );
  }

  if (!validity?.valid) {
    return (
      <div className="max-w-lg mx-auto mt-16 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">This link expired</h1>
          <p className="text-sm text-gray-600">
            Just message Monk again in Slack to get a fresh connect link.
          </p>
        </div>
      </div>
    );
  }

  if (connected) {
    return (
      <div className="max-w-lg mx-auto mt-16 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <CheckCircleIcon className="h-10 w-10 text-green-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Connected</h1>
          <p className="text-sm text-gray-600">
            Go back to Slack and say hi to Monk — the first reply may take a little longer while
            your agent warms up.
          </p>
          {tokenVerified === false && (
            <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              We saved your token but couldn&apos;t verify it just now. Message Monk to confirm
              it works — if it doesn&apos;t, come back and reconnect.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto mt-16 px-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Connect Monk</h1>
        <p className="text-sm text-gray-600 mb-4">
          Monk uses your own Claude subscription to answer questions. Your Monksheets access is
          set up automatically — you only need to supply your Claude setup token.
        </p>

        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <li>Install Claude Code</li>
          <li>
            Run <code className="font-mono bg-gray-100 px-1 rounded">claude setup-token</code>
          </li>
          <li>Paste the output below</li>
        </ol>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="claude-token" className="block text-sm font-medium text-gray-700 mb-1">
              Claude setup token
            </label>
            <textarea
              id="claude-token"
              value={claudeToken}
              onChange={(e) => setClaudeToken(e.target.value)}
              rows={4}
              placeholder="sk-ant-oat..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {(formError || connectBot.isError) && (
            <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {formError || 'Failed to connect Monk'}
            </div>
          )}

          <Button type="submit" isLoading={connectBot.isPending} className="w-full justify-center">
            Connect
          </Button>
        </form>
      </div>
    </div>
  );
}
